import React from "react";

import { CalendarIcon as Calendar } from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import { Link } from "ds/Button";
import { capitalize } from "Libs/utils";
import { meSelector } from "Reducers/app/selectors";
import {
  Grid as MetricsGrid,
  Layout as MetricsLayout
} from "src/organization/pages/project/environment/metrics/containers/Service/Service.styles";
import useMetric from "src/organization/pages/project/environment/metrics/hooks/useMetric";
import EmptyCard from "src/organization/pages/project/environment/services/components/EmptyCard";
import { useAppSelector } from "Store/hooks";

import serviceKindSelector from "./serviceKind.selector";
import * as S from "./ServiceMetrics.styles";

export interface ServiceMetricsProps {
  name: string;
}

const timeFrame = { id: "15m" };

const ServiceMetrics = ({ name }: ServiceMetricsProps) => {
  const location = useLocation();
  const { data, status, error } = useMetric(name, timeFrame);

  const serviceKind = useAppSelector(state =>
    serviceKindSelector(state, {
      ids: location.pathname.split("/").slice(1, 4) || [],
      name
    })
  );
  const me = useAppSelector(meSelector);
  const intl = useIntl();

  return (
    <S.Container>
      <S.Header>
        <S.Title tag="h3">
          <FormattedMessage id="metrics" />
        </S.Title>
        {!error && data && (
          <S.HeaderRight>
            <Link
              analyticId="view_metrics"
              to={`../metrics?range=15m&service=${name}`}
            >
              <FormattedMessage id="view_metrics" />
            </Link>
            <S.Timeframe>
              <Calendar />
              <FormattedMessage
                id="metrics.timepast"
                values={{
                  duration: 15,
                  durationText: intl.formatMessage({
                    id: "minutes"
                  })
                }}
              />
            </S.Timeframe>
          </S.HeaderRight>
        )}
      </S.Header>
      {status.refresh ? (
        <S.ServiceMetricsSkeleton />
      ) : (
        <MetricsLayout>
          {!data && !error ? (
            <S.EmptyMetrics>
              <FormattedMessage
                id="metrics.service.emtpy"
                values={{ serviceKind, serviceName: capitalize(name) }}
              />
              &nbsp;
              <Link
                analyticId="learnmore"
                href={intl.formatMessage({
                  id: "links.documentation.redeploy"
                })}
              >
                <FormattedMessage id="learnmore" />
              </Link>
            </S.EmptyMetrics>
          ) : error ? (
            <EmptyCard>
              {intl.formatMessage({ id: "services.metrics.error" })}. &nbsp;
              <FormattedMessage
                id="metrics.error.generic.link"
                values={{
                  a: chunks => (
                    <S.Link
                      analyticId="tickets_open"
                      target="_blank"
                      to={`/-/users/${me?.username}/tickets/open`}
                    >
                      {chunks}
                    </S.Link>
                  )
                }}
              />
            </EmptyCard>
          ) : (
            <MetricsGrid>
              {Object.entries(data ?? {}).map(
                ([metricName, metrics]: [string, any]) =>
                  metrics?.length && (
                    <S.ServiceMetric
                      key={metricName}
                      name={metricName}
                      value={metrics[metrics.length - 1]["average@value"]}
                      max={metrics[metrics.length - 1].max}
                      percentage={
                        metrics[metrics.length - 1]["average@percentage"]
                      }
                      data={metrics.map(value => ({
                        timestamp: value.timestamp,
                        value: isNaN(value["average@value"])
                          ? 0
                          : value["average@value"]
                      }))}
                      isLoading={status.refresh}
                    />
                  )
              )}
            </MetricsGrid>
          )}
        </MetricsLayout>
      )}
    </S.Container>
  );
};

export default ServiceMetrics;
